import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import Login from '../auth/Login';
import { teamPreview, joinTeamByToken } from '../tareas/api';
import { setPendingInvite } from './inviteStorage';
import { planLimitHint } from '../billing/limits';

type State =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'signup'; teamName: string }
  | { kind: 'joining'; teamName: string }
  | { kind: 'error'; msg: string };

export default function InviteLanding() {
  const { teamId = '', token = '' } = useParams();
  const { session, reloadProfile, switchTeam } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    (async () => {
      const teamName = await teamPreview(teamId, token).catch(() => null);
      if (!teamName) {
        setState({ kind: 'invalid' });
        return;
      }

      // No logueado: guardar invitación y mostrar el registro.
      if (!session) {
        setPendingInvite({ teamId, token, teamName });
        setState({ kind: 'signup', teamName });
        return;
      }

      // Logueado: unirse como espacio ADICIONAL y cambiar a él (multi-workspace).
      setState({ kind: 'joining', teamName });
      try {
        await joinTeamByToken(teamId, token);
        await reloadProfile();
        switchTeam(teamId);
        navigate('/bandeja', { replace: true });
      } catch (err) {
        const hint = planLimitHint(err);
        const msg = hint === 'member_limit_reached'
          ? 'Este espacio ha alcanzado el límite de usuarios de su plan. Pide a quien te invitó que amplíe a Team para que puedas unirte.'
          : (err instanceof Error ? err.message : 'No se pudo unir al equipo');
        setState({ kind: 'error', msg });
      }
    })();
  }, [teamId, token, session, reloadProfile, switchTeam, navigate]);

  if (state.kind === 'signup') {
    // El banner de invitación lo pinta Login leyendo la invitación pendiente.
    return <Login />;
  }

  return (
    <div style={styles.screen}>
      <div style={styles.card}>
        {state.kind === 'loading' && <p style={styles.muted}>Comprobando invitación…</p>}
        {state.kind === 'joining' && <p style={styles.muted}>Uniéndote a {state.teamName}…</p>}
        {state.kind === 'invalid' && (
          <>
            <h1 style={styles.title}>Invitación no válida</h1>
            <p style={styles.muted}>El enlace es incorrecto o ha caducado. Pide uno nuevo a quien te invitó.</p>
            <button type="button" style={styles.btn} onClick={() => navigate('/', { replace: true })}>Ir al inicio</button>
          </>
        )}
        {state.kind === 'error' && (
          <>
            <h1 style={styles.title}>No se pudo completar</h1>
            <p style={styles.error}>{state.msg}</p>
            <button type="button" style={styles.btn} onClick={() => navigate('/', { replace: true })}>Volver</button>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  screen: {
    minHeight: '100vh', background: '#f8fafc', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Söhne', 'Helvetica Neue', Helvetica, Arial, sans-serif", padding: 24,
  },
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16,
    padding: '40px 36px', width: 400, display: 'flex', flexDirection: 'column',
    gap: 14, boxShadow: '0 1px 3px rgba(15,23,42,0.06)', textAlign: 'center',
  },
  title: { fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 },
  muted: { color: '#64748b', fontSize: 14, margin: 0, lineHeight: 1.5 },
  error: { color: '#dc2626', fontSize: 14, margin: 0 },
  btn: {
    background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8,
    padding: '11px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 6,
  },
};
